import {addDays, startOfDay} from 'date-fns';
import {DataStatus, getTimeFromDate} from 'platform/components';

import {
  useGetTireSetDestinationBranchQuery,
  useGetTireSetRequestQuery,
  usePatchTireSetRequestMutation,
  usePostTireSetCancelRequestMutation,
  usePostTireSetRequestMutation,
} from '@dms/api/core';

import {parseDate, TestIdProps} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';
import {RequestTireSetData, RequestTireSetForm} from './RequestTireSetForm';

interface RequestTireSetProps extends TestIdProps {
  tireSetId: string;
  tireOrderId: string;
  onSave?: VoidFunction;
  onClose: VoidFunction;
}

export function RequestTireSet(props: RequestTireSetProps) {
  const {data, isLoading, isError} = useGetTireSetRequestQuery({
    orderId: props.tireOrderId,
    setId: props.tireSetId,
  });

  const {
    data: tireSetDestinationBranches,
    isLoading: isBranchesLoading,
    isError: isBranchesError,
  } = useGetTireSetDestinationBranchQuery({orderId: props.tireOrderId, setId: props.tireSetId});

  const [postTireSetRequest] = usePostTireSetRequestMutation();
  const [patchTireSetRequest] = usePatchTireSetRequestMutation();
  const [postTireSetCancelRequest] = usePostTireSetCancelRequestMutation();

  const handleSubmit = async (formData: RequestTireSetData) => {
    const requestData = {
      orderId: props.tireOrderId,
      setId: props.tireSetId,
      body: {...formData, destinationBranchId: formData?.requestBranchId},
    };

    await (data ? patchTireSetRequest(requestData) : postTireSetRequest(requestData))
      .unwrap()
      .then(() => props.onClose())
      .then(() => props.onSave?.())
      .catch(handleApiError);
  };

  const handleCancelRequest = () =>
    postTireSetCancelRequest({orderId: props.tireOrderId, setId: props.tireSetId})
      .unwrap()
      .then(() => props.onClose())
      .then(() => props.onSave?.())
      .catch(handleApiError);

  const defaultBranchId =
    data?.destinationBranchId ?? tireSetDestinationBranches?.find((b) => b.isDefault)?.branchId;

  const selectedBranch = tireSetDestinationBranches?.find((b) => b.branchId === defaultBranchId);

  const defaultNeededAtDate =
    parseDate(data?.neededAt) ??
    (selectedBranch ? addDays(startOfDay(new Date()), selectedBranch.transitTimeDays) : undefined);

  return (
    <DataStatus isLoading={isLoading || isBranchesLoading} isError={isError || isBranchesError}>
      <RequestTireSetForm
        tireOrderId={props.tireOrderId}
        tireSetId={props.tireSetId}
        tireSetDestinationBranches={tireSetDestinationBranches}
        defaultValues={{
          requestBranchId: defaultBranchId,
          note: data?.note,
          neededAtTime: data?.neededAt ? getTimeFromDate(parseDate(data.neededAt)) : '07:00',
          neededAtDate: defaultNeededAtDate,
        }}
        onSubmit={handleSubmit}
        onClose={props.onClose}
        onCancelRequest={data ? handleCancelRequest : undefined}
      />
    </DataStatus>
  );
}
